import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { Navigate, useLocation } from "react-router-dom";
import { ReactNode } from "react";
import { ROUTE_PATH } from "@/constants/routeConstants";
import { hasPermission, Permission, UserRole } from "@/constants/roles";
import { useVerifiedSession } from "@/hooks/useVerifiedSession";

interface ProtectedRouteProps {
  children: ReactNode;
  permission?: Permission;
  redirectTo?: string;
}

/**
 * ProtectedRoute component
 * Guards routes that require authentication with Azure Entra ID
 * Also supports role-based access control
 */
const ProtectedRoute = ({
  children,
  permission,
  redirectTo = ROUTE_PATH.ROUTE_LOGIN,
}: ProtectedRouteProps) => {
  const { isAuthenticated, user, loading } = useSelector(
    (state: RootState) => state.auth
  );

  // Use redux-persist's built-in _persist state
  const { _persist } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  // Redux alone is not trusted: the session must belong to this tab's Microsoft account
  const isVerified = useVerifiedSession();

  // Wait for redux-persist to rehydrate
  if (!_persist?.rehydrated) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  // A stored session for a different (or no) Microsoft account goes back through /login,
  // which re-establishes the session from the current Microsoft identity
  if (!isAuthenticated || !isVerified) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Role-based access control
  if (permission && user?.role) {
    const hasRequiredPermission = hasPermission(
      user.role as unknown as UserRole,
      permission
    );
    if (!hasRequiredPermission) {
      return <Navigate to={ROUTE_PATH.ROUTE_UNAUTHORIZED} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
